import type { TElementSpecifier } from "@de-xima/xima-common-js-dom";
import type Framebus from "framebus";
import type { IDisposable } from "monaco-editor";

/** Type for constructors. */
export type TConstructor<T = unknown> = new (...args: never[]) => T;

export type TAwaitable<T> = T | Promise<T>;

export interface IThenable<T> {
  then<R>(callback: (value: T) => R | IThenable<R>): IThenable<R>;
}

export interface IThenableUnknown<T> {
  then(callback: (value: T) => unknown | IThenableUnknown<unknown>): IThenableUnknown<unknown>;
}

/** Type for constructors. */
// biome-ignore lint/complexity/noBannedTypes: Generic type that needs to be flexible
export type TConstructorLike<T = unknown> = { prototype: T } & Function;

/**
 * @deprecated Use the package `@de-xima/xima-common-js-dom`
 */
export type TJQueryfiable = string | HTMLElement | JQuery | undefined | null | Document;

/**
 * Extracts the parameter array of key-value pairs for a given remote command.
 */
export type TRemoteCommandParameters<T> =
  T extends PrimeFaces.ajax.RemoteCommand<infer R> ? PrimeFaces.ajax.RemoteCommandParams<R> : never;

/**
 * Removes the head from a tuple.
 * ```typescript
 * type WithoutFirstItem = TTail<["a", "b", "c"]> // => ["b", "c"]
 * ```
 */
export type TTail<K extends string[]> = K extends [string, ...infer R] ? R : [];

/**
 * Accesses a nested property of an indexed type.
 * ```typescript
 * type TypeAtPath = TIndex<{a: {b: {c: number}}}, ["a", "b", "c"]> // => number
 * ```
 */
export type TIndex<T, K extends string[]> = K[0] extends keyof Exclude<T, undefined>
  ? TIndex<Exclude<T, undefined>[K[0]], TTail<K>> | (T extends undefined ? undefined : never)
  : T;

export interface IRemoteCommandParamsBase {
  remoteCommandError?: string;
  remoteCommandSuccess?: boolean;
}

export interface ISessionId {
  id: string;
  prefix: string;
}

// == General types == //
/**
 * A collector that can be used to reduce a list of a values.
 */
export type TReducer<T, R> = [(previousValue: R, newValue: T) => R, R];

/**
 * An intermediate value with a finish method to obtain the final value. Used
 * by {@link TFinishingReducer}.
 */
export type TIntermediate<R> = { finish: () => R };

/**
 * A supplier for an initial value.
 */
export type TSupplier<A> = () => A;
/**
 * An accumulator that merge a new value with an existing value and returns the
 * merged result.
 */
export type TAccumulator<T, A> = (previousValue: A, newValue: T) => A;
/**
 * A finisher that transforms an intermediate value into the final value.
 */
export type TFinisher<A, R> = (intermediate: A) => R;

/**
 * A collector that can be used to reduce a list of a values into an
 * intermediate value. The intermediate value has a finish method to obtain the
 * final value. This is a special {@link TReducer}.
 */
export type TFinishingReducer<T, R> = TReducer<T, TIntermediate<R>>;

// == Push channel listener module == //

export type TPushChannelListener = (message: unknown, channel: string, event: MessageEvent) => void;

export type TPushChannelCloseListener = (code: number, channel: string, event: CloseEvent) => void;

export type TPushChannelOpenListener = (channel: string) => void;

export type TPushEventListener = (payload: unknown, eventName: string, channel: string) => TAwaitable<void>;

/**
 * Options used when a request is made to show a dialog.
 */
export interface IHideDialogOptions {
  /**
   * When the dialog could not be found, ignore that and do not log any
   * warnings.
   * @default false
   */
  ignoreMissing?: boolean;
}

/**
 * Options used when a request is made to show a dialog.
 */
export interface IShowDialogOptions {
  /**
   * When `true` and the dialog is already visible, bounce it via
   * the JQuery UI bounce effect to the the user's attention to it.
   * @default false
   */
  bounceIfVisible?: boolean;
  /**
   * When true, ensure that the dialog is moved to the top, even when
   * already visible.
   * @default false
   */
  moveToTop?: boolean;
  /**
   * When the dialog could not be found, ignore that and do not log any
   * warnings.
   * @default false
   */
  ignoreMissing?: boolean;
}

export interface IPushChannelListener {
  onMessage: TPushChannelListener;
}

// == Framebus handler module == //

export interface IFramebusHandler {
  /** @returns The framebus instance to use. */
  getFramebus(): import("framebus");
}

export interface IEmbeddedFramebusHandler extends IFramebusHandler {
  /** @returns The ID of the embedded page. */
  getPageId(): string;
}

export interface IOpenedWindow {
  new: boolean;
  window: Window | undefined;
  windowId: string;
}

// == Scheduler extender module == //

/**
 * An additional button for full calendar that invokes another button when
 * clicked.
 */
export interface IScheduleProxyButton {
  proxy: string;
  icon?: string;
  text?: string;
}

/**
 * Options for the schedule extender.
 */
export interface IScheduleExtenderOptions {
  businessHours: import("@fullcalendar/core").EventInput[];

  /** Whether the start and end time of an event must be on the same day */
  constrainToSameDay: boolean;

  /**
   * When set, limits the number of events shown on a certain day to the given
   * value. Also adds a `more` button.
   */
  eventLimit: { month?: number; week?: number; day?: number };

  /**
   * The first day of the week. If unset, the default of the locale is used.
   */
  firstDay: number | undefined;

  /**
   * A remote command that deletes the event. Must take one parameter, the ID of
   * the event to be deleted. If this is set, each events gets an "x" button for
   * deleting that event.
   */
  deleteCallback: undefined | ((eventId: string) => void);

  /**
   * Optional title text for the delete button.
   */
  deleteTitle: string;

  /**
   * An object with buttons that are proxied. Adds a button definition to the
   * calendar that, when it is clicked, forwards the click to the proxied
   * button. You can then use the button name in the "header" or "footer"
   * template.
   */
  proxyButtons: Record<string, IScheduleProxyButton>;

  /**
   * When set to `true`, the calendar is resized automatically to fit its
   * container. You still need to apply the appropriate CSS to the container.
   */
  responsive: boolean;
}

// == Notification history module = //

export type TSeverity = "fatal" | "error" | "warn" | "info";

export type TSeverityId = 0 | 1 | 2 | 3;

// == Overlay searchbar module == //

export type TOnSearchbarClearListener<Candidate = unknown, Match = unknown> = (
  searchbar: IOverlaySearchbar<Candidate, Match>,
) => void;

/**
 * Controller for the `<xi:overlaySearchbar/>` widget. A search bar for filtering
 * or looking for content in a page.
 *
 * When the HTML is created via JSF, `OverlaySearchbar.init` is called with the
 * container element. This also adds a reference to the widget instance via `$.data`.
 *
 * Given the container element of the overlay search bar (with the class
 * `fc-overlay-searchbar`), access the widget as follows:
 *
 * ```javascript
 * $(".fc-overlay-searchbar").data("widget")
 * ```
 * Now you can call methods such as `show` and `hide` on the obtained widget
 * instance. Alternatively you can also access the search bar via the JQuery
 * wrapper:
 *
 * ```javascript
 * $(".fc-overlay-searchbar").searchbar("init");
 * $(".fc-overlay-searchbar").searchbar("show");
 * $(".fc-overlay-searchbar").searchbar("refreshSearch");
 * ```
 *
 * @typeparam Candidate Type of the candidates.
 * @typeparam Match Type of the matches.
 *
 */
export interface IOverlaySearchbar<Candidate = unknown, Match = unknown> {
  /**
   * If you only need the number of matching items, this is slightly faster than
   * `getMatches().length`.
   * @returns {number} How many candidates are currently matching the last search
   * that was performed.
   */
  getMatchCount(): number;

  /**
   * @returns A list of all items that currently match the search expression.
   */
  getMatches(): Match[];

  /**
   * Request this search bar to take the current search expression and performs
   * a new, full search.
   *
   * Several synchronous calls may be batched into one. If the searchbar is not
   * visible, refresh may be skipped.
   */
  requestRefresh(): void;

  /**
   * Takes the current search expression and performs a new, full search.
   */
  refreshSearch(): void;

  /**
   * @returns The currently entered search term.
   */
  getSearchExpression(): string;

  /**
   * @returns `true` iff the search is case-sensitive, `false` otherwise.
   */
  isMatchCase(): boolean;

  /**
   * @returns `true` iff the search term should match only whole word, `false`
   * otherwise.
   */
  isMatchWholeWord(): boolean;

  /**
   * @returns {boolean} True iff the search term is to be interpreted as a RegExp, false otherwise.
   */
  isUseRegexp(): boolean;

  /**
   * @returns `true` iff the auto highlighting is enabled (goto matched items as
   * soon as the user enters something).
   */
  isAutoHighlight(): boolean;

  /**
   * Registers a search handler with this searchbar instance. If no handler is
   * set, no useful search can be performed.
   * @param handler Handler to set.
   */
  setHandler(handler: IOverlaySearchbarHandler<Candidate, Match>): void;

  /**
   * @returns Whether this searchbar is currently visible.
   */
  isVisible(): boolean;

  /**
   * Shows this searchbar, if it is not visible already.
   */
  show(): void;

  /**
   * Shows the searchbar if hidden; and puts focus on the input field.
   */
  focus(): void;

  /**
   * Clears the search input field.
   */
  clear(): void;

  /**
   * Hide this searchbar, if it is not hidden already.
   */
  hide(): void;

  /**
   * @returns The container element of this searchbar.
   */
  getElement(): JQuery;

  /**
   * Positions this searchbar via the JQueryUI method `position`.
   * @param coordinates Coordinates that describe how to position this
   * searchbar.
   */
  position(coordinates: JQueryUI.JQueryPositionOptions): void;

  /**
   * Registers a listener for when the searchbar input field is cleared.
   * @param listener Listener to register.
   * @returns A disposable for removing the listener.
   */
  onClear(listener: TOnSearchbarClearListener<Candidate, Match>): IDisposable;

  /**
   * Removes a listener added via {@link IOverlaySearchbar.onClear}.
   * @param listener Listener to remove.
   */
  offClear(listener: TOnSearchbarClearListener<Candidate, Match>): void;
}

/**
 * Interface for a searchbar handler, as used by `IOverlaySearchbar`.
 * @typeparam Candidate Type of the candidates.
 * @typeparam Match Type of the matches.
 */
export interface IOverlaySearchbarHandler<Candidate, Match> {
  /**
   * Must return a list of all objects that are considered for the current
   * search. This list is then filtered according to the entered search term and
   * the search options.
   * @param searchbar The current searchbar instance.
   * @returns A list of candidate objects.
   */
  getCandidates(searchbar: IOverlaySearchbar<Candidate, Match>): Candidate[];

  /**
   * This method is called once for each candidate returned by `getCandidates`.
   * It must return the search text that is compared with the entered search
   * term. If a property is passed, must return only the search text for that
   * property of the candidate.
   *
   * @param searchbar The current searchbar instance.
   * @param candidate One of the candidate object as returned by
   * `getCandidates`.
   * @param index The index of the candidates in the list returned by
   * `getCandidates`.
   * @param property Which property of the item to get. May be undefined or
   * empty when no particular property was chosen.
   * @returns The text for filtering this candidate. May return `undefined` if no
   * text exists for the given item and property. May also be an object or
   * array, in which case each value of that array of object is searched.
   */
  getSearchText(
    searchbar: IOverlaySearchbar<Candidate, Match>,
    candidate: Candidate,
    index: number,
    property: string | undefined,
  ): unknown | undefined;

  /**
   * This method is passed all the candidates that match the search expression.
   * It must convert them to match objects. This allows for preprocessing the
   * candidates (mapping) or to change the order in which the matches are
   * displayed to the user.
   * @param searchbar The current searchbar instance.
   * @param candidates All candidates that match the search expression.
   * @returns The converted matches. This is not required to be a new array, it
   * may be the same object that was passed in. The array may also be modified
   * in-place.
   */
  convertCandidatesToMatches(searchbar: IOverlaySearchbar<Candidate, Match>, candidates: Candidate[]): Match[];

  /**
   * This method is called with those candidates that matched the search
   * expression. The user may navigate between the found matches.
   *
   * The side effect of this method must be to navigate to the given candidate
   * and/or to highlight it in some way.
   * @param searchbar The current searchbar instance.
   * @param match One of the candidate object as returned by `getCandidates`.
   * @param index The index of the candidates in the list returned by
   * `getCandidates`.
   */
  gotoMatch(searchbar: IOverlaySearchbar<Candidate, Match>, match: Match, index: number): void;

  /**
   * Called when a match that was focused via
   * {@link IOverlaySearchbarHandler.gotoMatch} loses focus. Implementations may
   * e.g. choose to remove highlighting for the the match if the match was
   * highlighted.
   * @param searchbar The current searchbar instance.
   * @param match The match which lost focus.
   * @param index The index of the candidates in the list returned by
   * `getCandidates`.
   */
  leaveMatch(searchbar: IOverlaySearchbar<Candidate, Match>, match: Match, index: number): void;

  /**
   * Called after a search was performed and matches were found. When the user
   * navigates to the next or previous match, this method is queried to find the
   * next match. This method must return the index of the next match. If it does
   * not return an index, a sensible default is used.
   * @param searchbar The current searchbar instance.
   * @param matches All matches that were found.
   * @param currentIndex The index of the currently highlighted match, if any.
   * @param direction 1 if navigating to the next match, -1 otherwise.
   * @returns An index in the list of matches (in the range
   * `[0, matches.length-1]`).
   */
  findNextMatch(
    searchbar: IOverlaySearchbar<Candidate, Match>,
    matches: Match[],
    currentIndex: number,
    direction: -1 | 1,
  ): number;

  stopSearch(search: IOverlaySearchbar<Candidate, Match>): void;
}

// == Placeholder module == //

export type TInsertionListener = (opts: IInsertionOpts) => void;

/**
 * Options for inserting text in an input field via the placeholder mask.
 */
export interface IInsertionOpts {
  /**
   * Whether to close the placeholder mask after insertion. Defaults to `false`.
   */
  close?: boolean | undefined;

  /**
   * Input component for inserting the placeholder. If not given, uses the
   * active component.
   */
  component?: TElementSpecifier<HTMLElement> | undefined;

  /**
   * Placeholder (value) to be inserted. If not given, uses the currently
   * selected placeholder.
   */
  placeholder: IInsertData;
}

/**
 * The placeholder mask is the panel that shows a list of all
 * available placeholders and lets the user select a placeholder
 * to be inserted. The abstract logic is handled by PlaceholderMask.
 * PlaceholderMask needs an instance of a AbstractMaskPanel that
 * handles the specific implementation and interaction with the DOM.
 * This is the base class for such a handler with the common methods
 * they need to implement.
 */
export interface IMaskPanel {
  /**
   * Registers a listener that must be called when a placeholder value should
   * be inserted.
   * @param insertionListener
   */
  setInsertionListener(insertionListener: TInsertionListener): void;

  /**
   * Closes this placeholder mask panel.
   */
  close(): void;

  /**
   * Callback invoked when an input element received focus.
   */
  focused(): void;

  /**
   * @returns The currently selected placeholder value,
   * or `undefined` iff none is selected.
   */
  getSelectedValue(): Promise<IInsertData | undefined>;

  /**
   * Called when any change (such as loading or updating component      * dynamically via AJAX) was performed that may necessitate
   * updating the state of this mask panel.
   */
  onExternalChange(): void;

  /**
   * Shifts focus to the placeholder mask, opening it if is not open already.
   * @param $position May contain no elements. If given, the
   * currently active input component, ie the input component for which
   * the placeholder mask is opened. May be used to position the mask
   * appropriately.
   */
  open($position: JQuery): void;

  /**
   * Updates this mask panel. This may involve fetching the latest placeholder items from the server and displaying
   * those.
   */
  update(): void;

  /**
   * @returns Whether this placeholder mask is currently open.
   */
  isOpen(): Promise<boolean>;
}

/**
 * Options for creating a new PlaceholderMask.
 */
export interface IPlaceholderMaskOptions {
  /** ID of the mask panel. */
  id: string;

  /** Placeholder mask panel instance to be used. */
  maskPanel: IMaskPanel;

  /**
   * If set to `true`, registers a listener for all elements
   * with the class `ui-placeholder-trigger` and opens the placeholder mask when
   * that element is clicked. This can be used for creating additional buttons
   * that open the placeholder mask.
   */
  initTriggerButtons: boolean;
}

/**
 * Callbacks that are passed to {@link IComponentHandler.initialize}.
 */
export interface IComponentHandlerCallbacks {
  /**
   * Callback function that is to be called by this handler when the placeholder
   * selection panel should be opened. Unless not otherwise possible, this
   * should happen only when the user presses `Ctrl`+`SPACE`.
   */
  onFocus(): void;

  /**
   * Callback function that is to be called when the component gains focus.
   */
  onPlaceholderRequested(): void;
}

export const InsertHints = ["text", "html", "link"] as const;

export type TInsertHint = "text" | "html" | "link";

export interface IInsertData {
  content: string;
  insertHint: TInsertHint;
}

/**
 * The placeholder mechanism works everywhere, irrespective of the kind of input
 * component or editor. Pressing a certain buttons brings up an overlay with all
 * available placeholders. Selecting a placeholder inserts it into the current
 * editing position of the input component.
 *
 * A handler is responsible for implementing the required interaction with a
 * particular input component. Methods of this class are called by the central
 * placeholder mask (inversion of control). This allows for new input components
 * to be added easily.
 */
export interface IComponentHandler {
  /**
   * Checks whether the given DOM element contains - or is itself,
   * a component of this type. When the type of input component is
   * not provided explicitly, this method is called for all known
   * handlers to determine the type of component. The result is
   * cached.
   * @param $element Input component into which to insert text.
   * @returns {boolean} Whether the given element contains a placeholder
   * component of this type.
   */
  accepts($element: JQuery): boolean;

  /**
   * Puts focus on the input component, if possible. Used to switch
   * back to the input component after a placeholder was selected.
   * @param  $element Input component that is to be focused.
   */
  focus($element: JQuery): void;

  /**
   * @param $element The input component.
   * @returns The element for the actual input component, eg.
   * the input tag, textarea tag, code mirror iframe etc.
   */
  getInputElement($element: JQuery): JQuery;

  /**
   * Initializes the input component. Called only once and only iff
   * the component was not yet initialized.
   * @param $element Input component that is to be initialized.
   * @param callbacks Callbacks that need to be called on certain events.
   * @returns {boolean} True if the component could be initialized. If `false`, might try again later.
   */
  initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean;

  /**
   * Inserts the given text into the component. Called after a placeholder was
   * selected and needs to be inserted into the input component at the current
   * position.
   *
   * How the text is inserted may depend on the component, but by default, the
   * behavior should be as follows:
   *
   * - If no text is currently selected, insert the text and move the cursor to
   * the end of the selected text
   * - If some text is currently selected, replace the selection with the text
   * to be inserted.
   *
   * @param $element Input component into which the text is to be
   * inserted.
   * @param data The text snippet to be inserted.
   */
  insertText($element: JQuery, data: IInsertData): void;
}

/**
 * Options for the DialogDataTableMaskPanel.
 */
export interface IDialogDataTableMaskPanelOptions {
  /**
   * Name of the widget variable for the dialog.
   */
  dialogWidgetVar: string;

  /**
   * Name of the widget variable for the data table.
   */
  tableWidgetVar: string;
}

/**
 * Options for the FramebusChildMaskPanel.
 */
export interface IFramebusChildMaskPanelOptions {
  /** The framebus to use for communication with the top window. */
  framebus: Framebus;
}

/**
 * Options for the FramebusTopMaskPanel.
 */
export interface IFramebusTopMaskPanelOptions {
  /** The framebus to use for communication with the child window. */
  framebus: Framebus;

  /** The actual mask panel performing the job. */
  maskPanel: IMaskPanel;
}

// == Drag & drop upload module == //

export interface IBeanUploadChecker {
  onAdd(file: File | undefined | null, callback: (processedFile: File) => void): void;
  cancel(callbackKey: number): void;
  proceed(callbackKey: number, ...filesToSkip: string[]): void;
}

export interface IUploadInfo {
  callbackKey: number;
  files: { name: string; size: number }[];
}

// == Monaco editor module == //

export type TCompletionItemProviderFactory = (
  options: import("monaco-editor").editor.IStandaloneEditorConstructionOptions,
) => import("monaco-editor").languages.CompletionItemProvider;

export interface ISnippet {
  body: string[];
  description?: string;
  prefix?: string;
}

export interface IMonacoJsonSchema {
  /**
   * The URI of the schema, which is also the identifier of the schema.
   */
  uri: string;
  /**
   * A list of glob patterns that describe for which file URIs the JSON schema will be used.
   * `*` and `**` wildcards are supported. Exclusion patterns start with `!`.
   * For example `*.schema.json`, `package.json`, `!foo*.schema.json`, `foo/**\/BADRESP.json`.
   * A match succeeds when there is at least one pattern matching and last matching pattern does not start with `!`.
   */
  fileMatch?: string[];
  /**
   * The schema for the given URI.
   */
  // biome-ignore lint/suspicious/noExplicitAny: need to use any to match monaco editor API
  schema?: any;
}

export interface IMonacoJsonExtraSchema {
  uri: string;
  schema?: Record<string, unknown> | undefined;
}

/**
 * Settings for creating a completion item provider that adds suggestions for
 * the given snippets to the monaco editor.
 */
export interface ISnippetCompletionProviderOptions {
  /**
   * Snippets to add. Must have been defined via
   * `Formcycle.MonacoEditor.registerSnippet`.
   */
  snippetNames: string[];
  /**
   * Optional replacer applied to the snippet text. Can be used e.g. to replace
   * placeholders in the snippet text.
   */
  textReplacer?: (text: string) => string;
}

/**
 * Options that can be passed when creating a monaco extender.
 */
export interface IMonacoExtendOptions {
  /**
   * Whether the editor is automatically resized when the container changes its
   * size.
   */
  autoResize: boolean;

  /**
   * Type definitions files to be loaded, file name only without the extensions.
   * They must be placed into `resources/formcycle/types/<name>.d.ts`
   */
  types: string[];

  /**
   * JSON only. Main schema to be applied to this monaco editor. The `uri` is
   * the identifier, the schema itself may be an object or a JSON string.
   */
  jsonSchema: IMonacoJsonSchema | string | undefined;

  /**
   * JSON only. Additional schemas to be applied to this monaco editor. The
   * `uri` is the identifier, the schema itself may be an object or a JSON string.
   */
  jsonAdditionalSchemas: IMonacoJsonExtraSchema[];

  /**
   * JSON only. Whether an attempt is made to load unresolved schema from the
   * specified URI. Note that this requires the cross origin header to be set
   * appropriately.
   */
  jsonEnableSchemaRequest: boolean;

  /**
   * JSON only. Whether JSON is validated.
   */
  jsonValidate: boolean;

  /**
   * JSON only. Severity of schema validation issues. Defaults to "warning".
   */
  jsonSchemaSeverity: "error" | "warning" | "ignore";

  /**
   * When `true`, forces the extender to perform its setup even when the
   * language was set up already.
   */
  forceSetup: boolean;

  /** Filename for the editor model that will be created  */
  filename: string;

  /**
   * When `true`, limits the suggestions for variables and function from the
   * global scope, especially many globals in a web context environment.
   */
  limitGlobalSuggestions: boolean;

  /**
   * Optional placeholder text to show when the editor has no value.
   */
  placeholder: string;

  /**
   * A map between the ID of a language and the completion item providers that
   * should be used for that language.
   */
  completionProviders: { [languageId: string]: (string | TCompletionItemProviderFactory)[] };
}

// == Designer frame bus handler module == //

export interface IDesignerPreviewSettings {
  previewDebugtools?: boolean;
  previewStatus?: string;
  previewUsergroup?: string;
}

export interface IUploadFileData {
  data: string;
  fileName: string;
}
